import { Injectable, isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OverlayModule } from '@angular/cdk/overlay';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import { MatDialogModule } from '@angular/material/dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MAT_DATE_LOCALE, MatRippleModule } from '@angular/material/core';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorIntl,
} from '@angular/material/paginator';
import { AppHomeComponent } from './section/home';
import { AppNotFoundComponent } from './section/not-found';
import {
  ServiceWorkerModule,
  SwUpdate,
  VersionReadyEvent,
} from '@angular/service-worker';
import { InputUploaderModule } from '@spd/input-uploader';
import {
  MAT_LUXON_DATE_ADAPTER_OPTIONS,
  provideLuxonDateAdapter,
} from '@angular/material-luxon-adapter';
import { AppNotifyModule } from '@app/notify';
import {
  API_DEFAULT_OPTIONS,
  ApiAuthModule,
  ApiModule,
  ApiTravelModule,
  NgxMatLuxonModule,
} from '@app/api';
import { environment } from '../environments/environment';
import { AppModalService } from '@app/modal';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { MicroSentryModule } from '@micro-sentry/angular';

@Injectable()
export class AppMatPaginatorIntl extends MatPaginatorIntl {
  public override itemsPerPageLabel = 'Nombre par page : ';
  public override nextPageLabel = 'Page suiv.';
  public override previousPageLabel = 'Page préc.';
  public override getRangeLabel = (
    page: number,
    pageSize: number,
    length: number,
  ): string => {
    if (length === 0 || pageSize === 0) {
      return `0 / ${length}`;
    }

    length = Math.max(length, 0);

    const startIndex: number = page * pageSize;
    const endIndex: number =
      startIndex < length
        ? Math.min(startIndex + pageSize, length)
        : startIndex + pageSize;

    return `${startIndex + 1} - ${endIndex} / ${length}`;
  };
}

const sentryModule = environment.production
  ? [
      MicroSentryModule.forRoot({
        dsn: 'https://64d538bd28cc4a5fb8a3a2ed34bbf493@glitchtip.supa-dev.com/3',
      }),
    ]
  : [];

@NgModule({
  declarations: [AppComponent, AppHomeComponent, AppNotFoundComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AppNotifyModule,
    ApiModule.forRoot(),
    ApiAuthModule.forRoot(),
    InputUploaderModule.forRoot(),
    ApiTravelModule.forRoot(),
    OverlayModule,
    ApiAuthModule,
    MatMenuModule,
    MatSidenavModule,
    MatButtonModule,
    MatSnackBarModule,
    NgxMatLuxonModule,
    MatDialogModule,
    MatGridListModule,
    MatIconModule,
    MatRippleModule,
    BrowserAnimationsModule,
    AngularFireModule.initializeApp({
      apiKey: 'AIzaSyB-hfmIbgppdKplWPGLIsVahgxl121Y2eY',
      authDomain: 'grooupee.firebaseapp.com',
      projectId: 'grooupee',
      storageBucket: 'grooupee.firebasestorage.app',
      messagingSenderId: '648939689065',
      appId: '1:648939689065:web:0ad562cfe8aa4f9a913ef8',
      measurementId: 'G-EQS4YWQYW8',
    }),
    AngularFireMessagingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    ...sentryModule,
  ],
  providers: [
    provideLuxonDateAdapter(),
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 10000 },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        pageSize: 1000,
        pageSizeOptions: [5, 10, 20, 30, 50, 100, 500, 1000],
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'fr-FR',
    },
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: {
        firstDayOfWeek: 1,
      },
    },
    {
      provide: MatPaginatorIntl,
      useClass: AppMatPaginatorIntl,
    },
    {
      provide: API_DEFAULT_OPTIONS,
      useValue: environment,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private _swUpdate: SwUpdate,
    private _appModal: AppModalService,
  ) {
    if (this._swUpdate.isEnabled) {
      console.log('Service worker is enabled.');
      this._swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY',
          ),
        )
        .subscribe(() => {
          console.log('Service worker update available.');
          this._appModal.confirm(
            'Nouvelle version disponible',
            'Souhaitez vous la télécharger ?',
            (confirmed) => {
              if (confirmed) {
                console.log('Service worker update activated.');
                document.location.reload();
              }
            },
          );
        });

      interval(60 * 1000).subscribe(() => {
        console.log('Service worker check for update.');
        this._swUpdate.checkForUpdate();
      });

      this._swUpdate.checkForUpdate();
    } else {
      console.log('Service worker is disabled.');
    }
  }
}
